import { createSlice } from "@reduxjs/toolkit"

// ── Persistence for the live FCM push bell — survives page refresh / new tabs ──
const STORAGE_KEY = "fcmLivePushState"
const MAX_STORED_ITEMS = 50

const loadPersistedPushState = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return { livePushList: [], unreadPushCount: 0 }

        const parsed = JSON.parse(raw)
        return {
            livePushList: Array.isArray(parsed?.livePushList) ? parsed.livePushList : [],
            unreadPushCount: typeof parsed?.unreadPushCount === "number" ? parsed.unreadPushCount : 0
        }
    } catch {
        // Corrupt or inaccessible storage — fall back to empty rather than crash
        return { livePushList: [], unreadPushCount: 0 }
    }
}

const persistPushState = (state) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            livePushList: state.livePushList,
            unreadPushCount: state.unreadPushCount
        }))
    } catch {
        // e.g. storage quota exceeded / disabled — non-fatal, just skip persisting
    }
}

export const NotificationSlice = createSlice({
    name: "Notification",
    initialState: {
        notificationList: [],
        singleDetail: {},
        isFreshData: false,
        totalEntry: 0,

        // ── live FCM push notifications for the admin bell dropdown ──
        // Hydrated from localStorage so the bell survives refreshes/new tabs.
        ...loadPersistedPushState(),
    },
    reducers: {
        setNotificationList: (state, action) => {
            state.notificationList = action.payload
        },
        setFreshList: (state, action) => {
            state.isFreshData = action.payload
        },
        setTotalEntry: (state, action) => {
            state.totalEntry = action.payload
        },
        setSingleData: (state, action) => {
            state.singleDetail = action.payload
        },

        addLivePush: (state, action) => {
            // action.payload: { id, title, subtitle, date, unread: true }
            // Guard against the same push being added twice (e.g. background
            // relay + a duplicate send from the backend landing with the same id)
            const alreadyExists = state.livePushList.some(item => item.id === action.payload.id)
            if (alreadyExists) return

            state.livePushList.unshift(action.payload)
            state.livePushList = state.livePushList.slice(0, MAX_STORED_ITEMS)
            state.unreadPushCount += 1

            persistPushState(state)
        },
        markAllPushRead: (state) => {
            state.livePushList = state.livePushList.map(item => ({ ...item, unread: false }))
            state.unreadPushCount = 0

            persistPushState(state)
        },
        clearLivePush: (state) => {
            state.livePushList = []
            state.unreadPushCount = 0

            persistPushState(state)
        }
    }
})

export const {
    setNotificationList,
    setFreshList,
    setSingleData,
    setTotalEntry,
    addLivePush,
    markAllPushRead,
    clearLivePush,
} = NotificationSlice.actions

export const selectNotificationList = (state) => state.notification.notificationList
export const selectFreshList = (state) => state.notification.isFreshData
export const selectTotalEntry = (state) => state.notification.totalEntry
export const selectSingleDetail = (state) => state.notification.singleDetail

// ── NEW ──
export const selectLivePushList = (state) => state.notification.livePushList
export const selectUnreadPushCount = (state) => state.notification.unreadPushCount

export default NotificationSlice.reducer